import { Injectable } from '@angular/core';
import { ControlPoint } from '../../../shared/models/controlPoint';
import { Observable, of, map, lastValueFrom } from 'rxjs';
import { CurrentStateService } from '../../../core/services/current-state.service';
import { HttpService } from '../../../core/services/http.service';
import { EPSG } from '../../../shared/models/epsg';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '@shared/models/api-response';
import { Threshold } from '@shared/models/threshold';

@Injectable({
  providedIn: 'root'
})
export class ControlPointsService {
  private cacheControlPoints = new Map<number, ControlPoint[]>();
  private cacheEPSG: EPSG[] = [];
  baseUrl = `${environment.exploreURL}/api`;
  vibrationAPI = environment.vibrationApiURL;

  constructor(
    private currentStateService: CurrentStateService,
    private httpClient: HttpClient) { }

  public getControlPointsBySiteId(siteId: number = 0, needRefreshCache: boolean): Observable<ControlPoint[]> {
    if (!needRefreshCache && this.cacheControlPoints.has(siteId)) {
      const data = this.cacheControlPoints.get(siteId);
      if (data) return of(data);
    }

    return this.httpClient.get<ControlPoint[]>(`${this.baseUrl}/Sites/${siteId}/ControlPoints?transformCoord=false`).pipe(
      map(response => {
        const transformedResponse = response.map(point => ({
          ...point
        }));
        this.cacheControlPoints.set(siteId, transformedResponse);
        return transformedResponse;
      })
    );
  }

  public getControlPointById(siteId: number, controlPointId: number): ControlPoint | undefined {
    const cacheControlPointsForSite = this.cacheControlPoints.get(siteId);
    if (cacheControlPointsForSite) {
      return cacheControlPointsForSite.find(x => x.controlPointId === controlPointId);
    }

    return undefined;
  }

  async getControlPointByIdAsync(controlPointId: number): Promise<ControlPoint | undefined> {
    try {
      const response = await lastValueFrom(
        this.httpClient.get<ControlPoint>(`${this.baseUrl}/ControlPoint/${controlPointId}`)
      );
      return response;
    } catch (error) {
      console.error('Error fetching control point:', error);
      return undefined;
    }
  }

  public postControlPoint(siteId: number, controlPoint: ControlPoint, epsg: string): Observable<ControlPoint> {
    return this.httpClient.post<ControlPoint>(`${this.baseUrl}/Sites/${siteId}/ControlPoint/${epsg}`, controlPoint);
  }

  public putControlPoint(siteId: number, controlPoint: ControlPoint, epsg: string): Observable<ControlPoint> {
    return this.httpClient.put<ControlPoint>(`${this.baseUrl}/Sites/${siteId}/ControlPoint/${epsg}`, controlPoint);
  }

  public addOrUpdateLocalControlPoint(controlPoint: ControlPoint) {
    // Check if cache exists for the given siteId
    if (this.cacheControlPoints.has(controlPoint.siteId)) {
      const cachedControlPoints = this.cacheControlPoints.get(controlPoint.siteId);
      if (cachedControlPoints) {
        // Check if the control point already exists
        const index = cachedControlPoints.findIndex(cp => cp.controlPointId === controlPoint.controlPointId);

        if (index !== -1) {
          // Update existing control point
          cachedControlPoints[index] = controlPoint;
        } else {
          // Add new control point
          cachedControlPoints.push(controlPoint);
        }
      }
    } else {
      // If no cache exists for this siteId, create a new entry
      this.cacheControlPoints.set(controlPoint.siteId, [controlPoint]);
    }

    // Notify that a new or updated control point has been created
    this.currentStateService.setNewControlPointCreated(controlPoint);
  }

  public getCachedControlPointsBySiteId(siteId: number) {
    if (this.cacheControlPoints.has(siteId)) {
      return this.cacheControlPoints.get(siteId) ?? [];
    } else {
      return [];
    }
  }

  deleteControlPoint(
    siteId: number,
    controlPointId: number,)
    : Observable<ApiResponse<boolean>> {
    return this.httpClient.delete<ApiResponse<boolean>>(
      `${this.baseUrl}/sites/${siteId}/ControlPoint/${controlPointId}`
    );
  }
  async getThreshold(serialNumber: string, siteId?: number, controlPointId?: number): Promise<Threshold | null> {
    try {
      const params: any = {};
  
      if (siteId !== undefined && siteId !== null) {
        params.siteId = siteId;
      }
  
      if (controlPointId !== undefined && controlPointId !== null) {
        params.controlPointId = controlPointId;
      }
      if (serialNumber !== undefined && serialNumber !== null) {
        params.serialNumber = serialNumber;
      }
  
      const response = await lastValueFrom(
        this.httpClient.get<ApiResponse<Threshold>>(
          `${this.vibrationAPI}/sites/threshold`,
          { params }
        )
      );
  
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching threshold:', error);
    }
    return null;
  }
  

  async addThreshold(threshold: Threshold): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.httpClient.post<ApiResponse<any>>(`${this.vibrationAPI}/sites/threshold`, threshold)
      );
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
    }
    return null;
  }
  async updateThreshold(threshold: Threshold): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.httpClient.put<ApiResponse<any>>(`${this.vibrationAPI}/sites/threshold`, threshold)
      );
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
    }
    return null;
  }

}
